import ContentConfig from '../../config/ContentConfig';
import DataReportBlock from './DataReportBlock';
import merge from 'lodash/merge';
import {
  TransferRecord,
  ConnectionRecord,
  PeerNotificationRecord,
  ContentNotificationRecord,
  NodeStateRecord,
  ClusterDetachRecord,
  PeerRequestRecord,
  AttachmentRequestRecord,
  SourceType,
  Component
} from '../../webrtc/agent/interfaces';
import {
  BitrateChange,
  EventReport
} from '../../common/Reporter/events/interfaces';
import { DetectionEvent, PrimingEvent } from '../constants';
import { LogReporterMessage } from '../../common/Reporter/report/interfaces';
import { PlayType } from '@kollective-frontend/ksdk-api';
import { Playback } from '../../common/constants';
import { ProxyConfig } from '../../config/ProxyConfig';

export interface o365Data {
  oid?: string;
  uniqueUserDigest?: string;
}

export interface MaybeGeneralBlock {
  playGuid?: string;
  v?: string;
  browserUserGuid?: string;
  labels?: string[];
  tags?: string[];
  localities?: string[];
  locationName?: string;
  user?: string;
  start?: number;
  integration?: { name: string };
  appName?: string;
  networkPolicies?: string[];
  playback?: Playback;
  playType?: PlayType;
  proxyConfig?: ProxyConfig;
  config?: ContentConfig;
  duration?: number;
  t?: number;
  timestamp?: number;
  reportIndex?: number;
  mergedReportIndices?: number[];
}

export class GeneralBlock implements MaybeGeneralBlock {
  readonly reporter: 'SDK';
  playGuid: string;
  v?: string;
  browserUserGuid?: string;
  labels?: string[];
  tags?: string[];
  localities?: string[];
  locationName?: string;
  t?: number;
  reportIndex: number;
  user?: string;
  mergedReportIndices?: number[];
  start?: number;
  integration?: { name: string };
  appName?: string;
  networkPolicies?: string[];
  playback?: Playback;
  playType?: PlayType;
  proxyConfig?: ProxyConfig;
  config?: ContentConfig;
  duration?: number;
  expedited?: string | null;
  critical?: string | null;
  timestamp: number;

  constructor(obj?: MaybeGeneralBlock) {
    this.reporter = 'SDK';
    this.playGuid = (obj && obj.playGuid) || '';
    this.v = obj && obj.v;
    this.config = obj && obj.config;
    this.browserUserGuid = obj && obj.browserUserGuid;
    this.user = obj && obj.user;
    this.start = obj && obj.start;
    this.integration = obj && obj.integration;
    this.appName = obj && obj.appName;
    this.playType = obj && obj.playType;
    this.playback = obj && obj.playback;
    this.labels = obj && obj.labels;
    this.tags = obj && obj.tags;
    this.localities = obj && obj.localities;
    this.locationName = obj && obj.locationName;
    this.t = obj && obj.t;
    this.duration = obj && obj.duration;
    this.reportIndex = obj?.reportIndex ?? 0;
    this.mergedReportIndices = obj?.mergedReportIndices;
    this.timestamp = obj?.timestamp ?? Date.now();
    this.proxyConfig = obj?.proxyConfig;
    this.networkPolicies = obj?.networkPolicies;
  }

  append(obj: MaybeGeneralBlock): void {
    // This replaces fields in this object with the ones from the incoming
    // object.
    merge(this, pick(obj, new GeneralBlock()));
  }
}

// Only copies keys that are part of the template object.
const pick = <T, M extends T>(obj: T, template: M): T => {
  type Key = keyof T;
  const copy = {} as Pick<T, Key>;
  // casting template is a hack as typescript doesn't like the T type in this function.
  const keys = Object.keys(template as Record<string, string>) as Key[];
  keys.forEach(key => (copy[key] = obj[key]));
  return copy as T;
};

export class EcdnAgent {
  v?: string;
  nodeId?: string;
  content?: string; // 10: urn, 11: guid

  constructor(obj?: EcdnAgent) {
    this.content = obj && obj.content;
    this.nodeId = obj && obj.nodeId;
    this.v = obj && obj.v;
  }
}

export class Ecdn11Agent extends EcdnAgent {
  activeClusterId?: string;
  agentId?: string;
  constructor(obj?: Ecdn11Agent) {
    super(obj);
    this.activeClusterId = obj?.activeClusterId;
    this.agentId = obj?.agentId;
  }
}

export class WebRtcBlock {
  activeClusterId?: string;
  v?: string;
  agentId?: string;
  nodeId?: string;

  constructor(obj?: WebRtcBlock) {
    this.activeClusterId = obj && obj.activeClusterId;
    this.v = obj && obj.v;
    this.agentId = obj && obj.agentId;
    this.nodeId = obj && obj.nodeId;
  }
}

export class Ecdn11Block {
  agent?: Ecdn11Agent; // Present iff delivery is through ECDN 11 agent
  content?: string;
  webrtc?: WebRtcBlock; // Present iff delivery is through ECDN 11 webrtc

  constructor(obj?: Ecdn11Block) {
    this.agent = new Ecdn11Agent(obj && obj.agent);
    this.content = obj && obj.content;
    this.webrtc = new WebRtcBlock(
      (obj && obj.webrtc) as unknown as WebRtcBlock
    );
  }
}

export interface TrimmedTransferRecord {
  // This allows for the minized transfer which removes tenant, content, object ids
  // Hence why it's copied and not exported
  type: SourceType;
  sourceId: string;
  nodeId: string;
  contentId?: string;
  objectId?: string;
  component: Component;
  timestamp: number;
  requestStart: number;
  loadInit: number;
  requestEnd: number;
  status: boolean;
  statusCode: number;
  tenantId?: string;
  clusterId: string | undefined;
  externalIp: string | undefined;
  internalIp: string | undefined;
  internalMdns: string | undefined;
  appName: string;
  playType: string | undefined;
  peerEnabled: boolean;
}

export interface PartialReport {
  general: GeneralBlock;
  ecdn11?: Ecdn11Block;
}

export interface IReport {
  general?: GeneralBlock;
  bitrates?: DataReportBlock<BitrateChange>;
  detection?: DataReportBlock<DetectionEvent>;
  events?: DataReportBlock<EventReport>;
  log?: DataReportBlock<LogReporterMessage>;
  priming?: DataReportBlock<PrimingEvent>;
  ecdn11?: Ecdn11Block;
  transfers?: DataReportBlock<TransferRecord | TrimmedTransferRecord>;
  connections?: DataReportBlock<ConnectionRecord>;
  peerNotifications?: DataReportBlock<PeerNotificationRecord>;
  contentNotifications?: DataReportBlock<ContentNotificationRecord>;
  detachments?: DataReportBlock<ClusterDetachRecord>;
  nodeState?: DataReportBlock<NodeStateRecord>;
  peerRequests?: DataReportBlock<PeerRequestRecord>;
  attachmentRequests?: DataReportBlock<AttachmentRequestRecord>;
}

export class Report implements PartialReport {
  // A token is added in the formatReportRequest
  general: GeneralBlock;
  bitrates?: DataReportBlock<BitrateChange>;
  detection?: DataReportBlock<DetectionEvent>;
  events?: DataReportBlock<EventReport>;
  log?: DataReportBlock<LogReporterMessage>;
  priming?: DataReportBlock<PrimingEvent>;
  ecdn11?: Ecdn11Block;
  transfers: DataReportBlock<TransferRecord | TrimmedTransferRecord>;
  connections: DataReportBlock<ConnectionRecord>;
  peerNotifications: DataReportBlock<PeerNotificationRecord>;
  contentNotifications: DataReportBlock<ContentNotificationRecord>;
  detachments: DataReportBlock<ClusterDetachRecord>;
  nodeState: DataReportBlock<NodeStateRecord>;
  peerRequests: DataReportBlock<PeerRequestRecord>;
  attachmentRequests: DataReportBlock<AttachmentRequestRecord>;

  constructor(obj?: IReport) {
    this.bitrates = new DataReportBlock(obj?.bitrates);
    this.detection = new DataReportBlock(obj?.detection);
    this.events = new DataReportBlock(obj?.events);
    this.log = new DataReportBlock(obj?.log);
    this.priming = new DataReportBlock(obj?.priming);
    this.general = new GeneralBlock(obj?.general);
    this.ecdn11 = new Ecdn11Block(obj?.ecdn11);
    this.transfers = new DataReportBlock(obj?.transfers);
    this.connections = new DataReportBlock(obj?.connections);
    this.peerNotifications = new DataReportBlock(obj?.peerNotifications);
    this.contentNotifications = new DataReportBlock(obj?.contentNotifications);
    this.nodeState = new DataReportBlock(obj?.nodeState);
    this.detachments = new DataReportBlock(obj?.detachments);
    this.peerRequests = new DataReportBlock(obj?.peerRequests);
    this.attachmentRequests = new DataReportBlock(obj?.attachmentRequests);
  }
}
